import { Injectable } from '@angular/core';
import { MuiModalService } from '@move/ui';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { MmuFeatureFlagsService } from '../../feature-flags';
import { MmuCookiePreferencesDialog } from '../cookie-dialog/mmu-cookie-preferences-dialog.component';
import { MmuFFEntGTMTrackingCookiePreferencesDialog } from '../FFEntGTMTrackingDialog/mmu-cookie-preferences-dialog.component';
import { MmuCookiePreferences } from '../mmu-cookie-preferences';


@Injectable()
export class MmuCookiePreferencesService {

  static readonly cookieName = 'user_cookie_settings';
  static readonly cookiePath = '/';

  get settingsAvailable(): boolean {
    return this._settings$.getValue() !== null;
  }

  get settings(): MmuCookiePreferences | null {
    return this._settings$.getValue();
  }

  get settings$(): Observable<MmuCookiePreferences | null> {
    return this._settings$.asObservable();
  }

  private _settings$: BehaviorSubject<MmuCookiePreferences | null> = new BehaviorSubject<MmuCookiePreferences | null>(null);

  constructor(private _cookieService: CookieService, private _modal: MuiModalService, private featureFlagsService: MmuFeatureFlagsService) {
    this.load();
  }

  /**
   * Check if the MmuCookiePreferences cookie has been set. If not, open a modal dialog that requests the user to choose and
   * save his preferences.
   */
  requestCookiePreferences(): void {
    if (!this.settingsAvailable) {

      const modalComponent = this.featureFlagsService.isFeatureEnabled('entGTMTracking') ? MmuCookiePreferencesDialog
                                                                                         : MmuFFEntGTMTrackingCookiePreferencesDialog;
      const modal = this._modal.open(
        modalComponent,
        {
          size: 'lg',
          centered: true,
          backdrop: 'static'
        }
      );

      const component: MmuFFEntGTMTrackingCookiePreferencesDialog = modal.componentInstance;
      /**
       *  TODO Refactor to handle modal result using the promise returning from the modal instead of subscribing to the settingsChanged observable
       */
      const subscription = component.settingsChanged.subscribe(settings => {
        this.save(settings);
      });

      // Clean up the subscription when the modal is closed
      modal.result.then(() => subscription.unsubscribe());
    }
  }

  /**
   * Load the MmuCookiePreferences cookie from the browser.
   */
  load(): MmuCookiePreferences | null {
    if (this._cookieService.check(MmuCookiePreferencesService.cookieName)) {
      try {
        const cookies = this._cookieService.get(MmuCookiePreferencesService.cookieName);
        this._settings$.next(JSON.parse(cookies));
      } catch (e) {
        console.error('CookieSettingsService: Failed to read user cookie settings.', e);
        this._settings$.next(null);
      }
    } else {
      this._settings$.next(null);
    }

    return this._settings$.getValue();
  }

  /**
   * Store the MmuCookiePreferences cookie in the browser.
   */
  save(settings: MmuCookiePreferences): void {
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + 1);

    this._cookieService.set(
      MmuCookiePreferencesService.cookieName,
      JSON.stringify(settings),
      expirationDate,
      MmuCookiePreferencesService.cookiePath,
      undefined,
      location.protocol === 'https:',
      'Strict'
    );

    this._settings$.next(settings);
  }
}
